import React from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ChevronDown, Globe } from 'lucide-react';

interface LanguageSwitcherProps {
  /** Set to false when used inside a Sheet/Dialog to prevent focus-lock conflicts */
  modal?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ modal }) => {
  const { language, setLanguage, languages } = useLanguage();
  
  const currentLang = languages.find(l => l.code === language);

  return (
    <DropdownMenu modal={modal}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-full px-3 py-2 text-sm font-medium transition-colors duration-200 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">{currentLang?.nativeName}</span>
          <span className="sm:hidden">{currentLang?.code.toUpperCase()}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 z-[9999]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code, false)}
            className={`cursor-pointer gap-3 ${
              language === lang.code 
                ? 'bg-primary/10 text-primary font-semibold' 
                : 'text-foreground/80 hover:!bg-primary/10 hover:!text-primary'
            }`}
          >
            <span className={`text-[11px] font-mono font-bold uppercase tracking-wider w-7 ${
              language === lang.code ? 'text-primary' : 'text-muted-foreground'
            }`}>
              {lang.code}
            </span>
            <span className="flex-1 font-medium">{lang.nativeName}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
